/**
 * Direitos do titular (task D16, art. 18 da LGPD).
 *
 * ── O pedido é o recurso, e não a operação ────────────────────────────────
 *
 * Não há `POST /v1/customers/{id}/anonimizar`. O que se cria é um PEDIDO, e a
 * execução é uma transição dele.
 *
 * A diferença aparece no dia em que o titular pergunta "vocês eliminaram meus
 * dados?": com a operação como recurso, a resposta é um efeito no banco sem
 * quem pediu, quando, por qual canal, nem quem executou. Com o pedido, a
 * resposta é uma linha.
 */

import { z } from 'zod';
import { IdSchema } from './comum.js';

export const TIPOS_DE_PEDIDO = [
  'confirmacao_e_acesso',
  'correcao',
  'anonimizacao',
  'eliminacao',
  'portabilidade',
  'informacao_de_compartilhamento',
  'revogacao_de_consentimento',
  'oposicao',
] as const;

export const CriarPedidoDoTitularSchema = z
  .object({
    tipo: z.enum(TIPOS_DE_PEDIDO),
    /**
     * Quem pediu, e por onde. Texto porque o titular pode não ter conta
     * nenhuma no sistema — e recusar por isso perderia o prazo do art. 18.
     */
    solicitante: z.string().min(3).max(320),
    canal: z.string().min(2).max(60),
    /** Nulo enquanto o titular não foi identificado. O prazo corre igual. */
    cliente_id: IdSchema.nullable().optional(),
    /** Obrigatória em revogação e oposição: qual finalidade parar. */
    finalidade: z.string().min(1).max(120).optional(),
  })
  .strict();

export const ExecutarPedidoDoTitularSchema = z
  .object({
    /**
     * Só em `correcao`. Campos do TITULAR, e não qualquer coluna: sem essa
     * limitação, "corrija meu nome" viraria um UPDATE arbitrário.
     */
    correcoes: z.record(z.string(), z.string()).optional(),
    /** Só em revogação e oposição. */
    finalidade: z.string().min(1).max(120).optional(),
  })
  .strict();

export const RecusarPedidoDoTitularSchema = z
  .object({
    /**
     * O art. 18, §4º dá ao titular o direito de saber por quê. Mínimo de dez
     * caracteres porque "não" não é motivo.
     */
    motivo: z.string().min(10).max(500),
  })
  .strict();

export const ReciboSchema = z.object({
  sistema: z.enum(['api-core', 'crm', 'chatwoot']),
  em: z.string(),
  resultado: z.enum(['eliminado', 'nada_a_fazer', 'falhou']),
  /** O que foi feito, em uma frase. Nunca o dado eliminado. */
  detalhe: z.string(),
});

export const PedidoDoTitularSchema = z.object({
  id: IdSchema,
  tipo: z.enum(TIPOS_DE_PEDIDO),
  estado: z.enum(['recebida', 'em_execucao', 'concluida', 'recusada']),
  recebida_em: z.string(),
  prazo_limite: z.string(),
  cliente_id: IdSchema.nullable(),
  /** Uma entrada por sistema. É o que a revenda apresenta ao titular. */
  recibos: z.array(ReciboSchema),
  /** Negativo é prazo vencido — e o pedido continua na fila. */
  dias_restantes: z.number().int(),
});

export const PaginaDePedidosDoTitularSchema = z.object({
  itens: z.array(PedidoDoTitularSchema),
});

/** Um bloco da exportação: uma tabela nossa, com as linhas do titular. */
export const BlocoExportadoSchema = z.object({
  tabela: z.string(),
  linhas: z.array(z.record(z.string(), z.unknown())),
});

export const ExportacaoDoTitularSchema = z.object({
  gerada_em: z.string(),
  cliente_id: IdSchema,
  formato: z.literal('cliqcrm.titular.v1'),
  dados: z.array(BlocoExportadoSchema),
  /** De onde o dado veio (art. 18, II). */
  origem: z.array(
    z.object({
      sistema: z.string(),
      conta: z.string(),
      idExterno: z.string(),
      tipo: z.string(),
      detalhe: z.record(z.string(), z.unknown()),
      vistoPrimeiroEm: z.string().nullable(),
    }),
  ),
  /** Com quem foi compartilhado (art. 18, VII). */
  operadores: z.array(z.object({ nome: z.string(), papel: z.string(), porque: z.string() })),
  consentimentos: z.array(z.record(z.string(), z.unknown())),
  /**
   * O que NÃO está aqui, e por quê.
   *
   * Exportação silenciosamente incompleta faz o titular assinar que recebeu
   * tudo sem ter recebido.
   */
  nao_incluido: z.array(z.object({ o_que: z.string(), porque: z.string() })),
});

export const ResultadoDoPedidoSchema = z.object({
  pedido: PedidoDoTitularSchema,
  /** Só em confirmação, acesso e portabilidade. */
  exportacao: ExportacaoDoTitularSchema.optional(),
});

export type CriarPedidoDoTitular = z.infer<typeof CriarPedidoDoTitularSchema>;
export type ExecutarPedidoDoTitular = z.infer<typeof ExecutarPedidoDoTitularSchema>;
export type RecusarPedidoDoTitular = z.infer<typeof RecusarPedidoDoTitularSchema>;
export type PedidoDoTitular = z.infer<typeof PedidoDoTitularSchema>;
export type PaginaDePedidosDoTitular = z.infer<typeof PaginaDePedidosDoTitularSchema>;
export type ExportacaoDoTitularDaApi = z.infer<typeof ExportacaoDoTitularSchema>;
export type ResultadoDoPedidoDaApi = z.infer<typeof ResultadoDoPedidoSchema>;
